import { Ajax } from "./modules/ajax/ajax.js";
import { Dialog } from "./modules/dialog/dialog.js";
import { Toast } from "./modules/dialog/toast.js";
import { Razor } from "./modules/razor/razor.js";
import { FileUploader } from "./modules/io/fileuploader.js";
import { Browser } from "./modules/core/browser.js";
import { DbInterfacer } from "./modules/database/db-interfacer.js";
import { hljs as CodeHighlight } from "./highlight.min.js";
import { default as ContextMenu } from "./modules/components/contextmenu/context-menu.js";

const configSetter = {
  set(target, property, value) {
    if (target[property] !== value) {
      target[property] = value;
      onConfigValueChanged(property, value);
    }

    return true;
  }
}

export const config = new Proxy({
  isDevelopment: document.documentElement.dataset.testMode?.toLowerCase() === "true",
  flavour: document.documentElement.dataset.flavour || "default",
  theme: document.documentElement.dataset.theme ?? "system",
  colour: document.documentElement.dataset.colour?.toLowerCase() ?? "blue"
}, configSetter);

const localizationHandler = {
  get(target, key) {
    return key in target ? target[key] : key;
  },
  set(target, key, value) {
    return Reflect.set(target, key, value);
  }
};

Object.assign(global, {
  Localizer: new Proxy({}, localizationHandler),
  Ajax, Dialog, Toast, ContextMenu,
  FileUploader, Razor, Browser, DbInterfacer, CodeHighlight
});

Event.prototype.halt = function () {
  this.stopPropagation();
  this.stopImmediatePropagation();
}

HTMLElement.prototype.wrapWithElement = function (nodeName, attributes) {
  const wrapper = Object.assign(document.createElement(nodeName), attributes);
  this.insertAdjacentElement("beforebegin", wrapper);

  wrapper.appendChild(this);
  return wrapper;
}

Document.prototype.createElementWithContents = function (nodeName, content, attributes) {
  const element = Object.assign(this.createElement(nodeName), attributes);

  if (content instanceof NodeList || content instanceof HTMLCollection || Array.isArray(content)) { element.append(...[...content].filter(x => x != null)); }
  else if (content instanceof Node) { element.appendChild(content); }
  else if (content != null) { element.innerHTML = content.toString(); }

  return element;
}

HTMLElement.prototype.toggleClass = function (className, add) {
  if (add == null) {
    add = !this.classList.contains(className);
  }

  if (add) {
    this.classList.add(className);
  } else {
    this.removeClass(className);
  }
}

function replaceURLs(element) {
  const urlRegex = /ht{2}ps?:\/\/(?:w{3}\.|(?!w{3}))[a-zA-Z\d][a-zA-Z\d-]+[a-zA-Z\d]\.\S{2,}|w{3}\.[a-zA-Z\d][a-zA-Z\d-]+[a-zA-Z\d]\.\S{2,}|ht{2}ps?:\/\/(?:w{3}\.|(?!w{3}))[a-zA-Z\d]+\.\S{2,}|w{3}\.[a-zA-Z\d]+\.\S{2,}/gi;

  function processTextNode(textNode) {
    const text = textNode.nodeValue;
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    text.replace(urlRegex, (url, offset) => {
      if (url.endsWith(")")) { url = url.slice(0, -1); }

      const displayURL = url;

      if (url.toLowerCase().startsWith("www.")) {
        url = "//" + url;
      }

      if (lastIndex < offset) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, offset)));
      }

      fragment.appendChild(document.createElementWithContents("chip-link", displayURL, {
        rel: "noopener",
        target: "_blank",
        href: url
      }));

      lastIndex = offset + displayURL.length;
    });

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    textNode.replaceWith(fragment);
  }

  function traverseNodes(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      processTextNode(node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      Array.from(node.childNodes).forEach(traverseNodes);
    }
  }

  traverseNodes(element);
}

function replaceEmails(element) {
  const emailRegex = /[a-z\d!#$%&'*/=?^_`{|}~-]+(?:\.[a-z\d!#$%&'*/=?^_`{|}~-]+)*@(?:[a-z\d](?:[a-z\d-]*[a-z\d])?\.)+[a-z\d](?:[a-z\d-]*[a-z\d])?/ig;

  function processTextNode(textNode) {
    const text = textNode.nodeValue;
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    text.replace(emailRegex, (email, offset) => {
      if (lastIndex < offset) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, offset)));
      }

      fragment.appendChild(document.createElementWithContents("chip-link", email, {
        href: `mailto:${email}`
      }));

      lastIndex = offset + email.length;
    });

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    textNode.replaceWith(fragment);
  }

  function traverseNodes(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      processTextNode(node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      Array.from(node.childNodes).forEach(traverseNodes);
    }
  }

  traverseNodes(element);
}

HTMLElement.prototype.enhance = function () {
  replaceURLs(this);
  replaceEmails(this);
}

function trimBreaksFromElement(element) {
  let lines = element.textContent.split('\n');

  if (lines.at(0) === "") { lines.shift(); }

  let matches,
    indentation = (matches = /^\s+/.exec(lines[0])) !== null ? matches[0] : null;

  if (indentation) {
    lines = lines.map(line => line.replace(indentation, '').replace(/\t/g, '    '));
    element.textContent = lines.join('\n').trimStart();

    if (!element.nextSibling) { element.textContent = element.textContent.trimEnd(); }
  }
}

HTMLElement.prototype.trimBreaks = function () {
  trimBreaksFromElement(this);
}

Node.prototype.trimBreaks = function () {
  trimBreaksFromElement(this);
}

HTMLElement.prototype.removeClasses = function (...classNames) {
  this.classList.remove(classNames);

  if (this.className === "") {
    this.removeAttribute("class");
  }
}

HTMLElement.prototype.removeClass = function (className) {
  this.removeClasses(className);
}

function similarity(s1, s2) {
  let longer = s1;
  let shorter = s2;

  if (s1.length < s2.length) {
    longer = s2;
    shorter = s1;
  }

  const longerLength = longer.length;

  if (longerLength == 0) {
    return 1.0;
  }
  return (longerLength - editDistance(longer, shorter)) / parseFloat(longerLength);
}

function editDistance(s1, s2) {
  s1 = s1.toLowerCase();
  s2 = s2.toLowerCase();

  const costs = [];

  for (let i = 0; i <= s1.length; i++) {
    let lastValue = i;

    for (let j = 0; j <= s2.length; j++) {
      if (i == 0) {
        costs[j] = j;
      }
      else if (j > 0) {
        let newValue = costs[j - 1];

        if (s1.charAt(i - 1) != s2.charAt(j - 1)) {
          newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
        }

        costs[j - 1] = lastValue;
        lastValue = newValue;
      }
    }

    if (i > 0) {
      costs[s2.length] = lastValue;
    }
  }

  return costs[s2.length];
}

const isScrollable = node => {
  if (!(node instanceof HTMLElement || node instanceof SVGElement)) {
    return false
  }

  const style = getComputedStyle(node);

  return ['overflow', 'overflow-x', 'overflow-y'].some((propertyName) => {
    const value = style.getPropertyValue(propertyName)
    return value === 'auto' || value === 'scroll'
  });
}

global.IsURL = function (value) {
  return /ht{2}ps?:\/\/(?:w{3}\.|(?!w{3}))[a-zA-Z\d][a-zA-Z\d-]+[a-zA-Z\d]\.\S{2,}|w{3}\.[a-zA-Z\d][a-zA-Z\d-]+[a-zA-Z\d]\.\S{2,}|ht{2}ps?:\/\/(?:w{3}\.|(?!w{3}))[a-zA-Z\d]+\.\S{2,}|w{3}\.[a-zA-Z\d]+\.\S{2,}/i.test(value.trim());
}

global._setFlavourLocalization = function (flavour = "default") {
  flavour ||= "default";

  if (flavour === "default") {
    Object.assign(Localizer, {
      ERROR_TITLE: "Error",
      ERROR_MESSAGE: "Something went wrong, and we're not sure what it was. You can either try again or report this error to an administrator if it persists."
    });
  } else {
    Object.assign(Localizer, {
      ERROR_TITLE: "There was a problem",
      ERROR_MESSAGE: "An unknown error has caused the application to stop working. If you continue to see this error, contact us."
    });
  }

  Localizer.FILTER_REMOVE = "Remove";

  Localizer.LINK_NEW_WINDOW = "(opens in new window)";
  Localizer.CARD_IMAGE_PREVIEW = "Card image preview";
  Localizer.CAROUSEL_PREVIOUS = "Previous slide";
  Localizer.CAROUSEL_NEXT = "Next slide";

  Localizer.DROPDOWN_NO_SEARCH_TITLE = "Make a search";
  Localizer.DROPDOWN_NO_SEARCH_DESCRIPTION = "Try searching to see results";
  Localizer.DROPDOWN_EMPTY_SEARCH_TITLE = "No results found";
  Localizer.DROPDOWN_EMPTY_SEARCH_DESCRIPTION = "Adjust your search to try again";
  Localizer.DROPDOWN_SEARCH_BAR_PLACEHOLDER = "Search";

  Localizer.SHOW_PASSWORD_BUTTON = "Show password";
  Localizer.HIDE_PASSWORD_BUTTON = "Hide password";

  Localizer.SIDEBAR_DESCRIPTION = "Application sidebar";
  Localizer.TOGGLE_SIDEBAR_BUTTON = "Toggle sidebar";
  Localizer.SIDEBAR_ACCORDION_DESCRIPTION = "Toggle more options";

  Localizer.DIALOG_BUTTON_CLOSE = "Close";
  Localizer.DIALOG_BUTTON_AFFIRMATIVE = "Yes";
  Localizer.DIALOG_BUTTON_NEGATIVE = "No";

  Localizer.THEME_BUTTON_DARK = "Change to light mode";
  Localizer.THEME_BUTTON_LIGHT = "Change to dark mode";
  Localizer.TOGGLE_DROPDOWN = "Toggle dropdown";

  Localizer.CONFIRMATION_TITLE = "Are you sure?";
  Localizer.WARNING_TITLE = "Warning";
  Localizer.SUCCESS_TITLE = "Success";

  Localizer.DROPDOWN_REQUIRED = "Please select an option";
  Localizer.COPIED_VALUE = "Copied to clipboard successfully";
  Localizer.TOGGLE_SIDEBAR = "Toggle sidebar";

  Localizer.BAD_REQUEST = "Something about your request wasn't recognized. Please try again later, and report this issue to an administrator if it persists.";
  Localizer.NOT_FOUND = "We couldn't seem to find the resource you requested. It could be that it doesn't exist, or it has been deleted.";
  Localizer.ACCESS_DENIED = "It doesn't look like you've got permission to do that. If you believe this is a mistake, please contact your system administrator.";
  Localizer.ACCESS_DENIED_TITLE = "Access Denied";

  Object.assign(Dialog.DefaultOptions, {
    ButtonText: Localizer.DIALOG_BUTTON_CLOSE,
    AffirmativeText: Localizer.DIALOG_BUTTON_AFFIRMATIVE,
    NegativeText: Localizer.DIALOG_BUTTON_NEGATIVE
  });
}

global.getScrollParent = node => {
  let currentParent = node.parentElement

  while (currentParent) {
    if (isScrollable(currentParent)) {
      return currentParent
    }

    currentParent = currentParent.parentElement
  }

  return document.scrollingElement || document.documentElement
}

global.getClipboardText = async function () {
  try {
    const state = (await navigator.permissions.query({ name: 'clipboard-read' })).state;

    return state !== "denied"
      ? await navigator.clipboard.readText()
      : "";
  } catch {
    // Firefox doesn't support reading from the clipboard
    return "";
  }
}

global.getClipboardData = async function () {
  try {
    const state = (await navigator.permissions.query({ name: 'clipboard-read' })).state;

    return state !== "denied"
      ? await navigator.clipboard.read()
      : [];
  } catch {
    // Firefox being Firefox
    return [];
  }
}

global.writeClipboardText = async function (text) {
  try {
    const state = (await navigator.permissions.query({ name: 'clipboard-write' })).state;

    if (state !== "denied") {
      await navigator.clipboard.writeText(text);
    }
  } catch {
    await navigator.clipboard.writeText(text);
  }
}

global.writeClipboard = async function (items) {
  try {
    const state = (await navigator.permissions.query({ name: 'clipboard-write' })).state;

    if (state !== "denied") {
      await navigator.clipboard.write(items);
    }
  } catch {
    await navigator.clipboard.write(items);
  }
}

global.capitalise = function (str) {
  return String(str).charAt(0).toUpperCase() + String(str).slice(1);
}

global.toTitleCase = function (str) {
  if (!str) { return str; }

  let upper = true;
  let newStr = "";

  for (let i = 0, l = str.length; i < l; i++) {
    if (str[i] == " ") {
      upper = true;
      newStr += str[i];

      continue;
    }

    newStr += upper ? str[i].toUpperCase() : str[i].toLowerCase();
    upper = false;
  }

  return newStr;
}

global.SortCaseInsensitive = function (a, b) {
  return a.toLowerCase().localeCompare(b.toLowerCase());
}

global.GroupBy = (array, fn) => array.reduce((a, b, i) => {
  const key = fn(b, i, array);

  a[key] ||= [];
  a[key].push(b);

  return a;
}, {});

global.SearchArray = function (array, query, propertyName, minLength) {
  const splitQuery = query.toUpperCase().split(" "),
    matchWholeProp = query.length < minLength;

  let arrSearched = array.
    map(x => {
      const comparator = propertyName ? x[propertyName] : x;

      const obj = {
        IsMatch: false,
        OriginalElement: x,
        MatchLevel: similarity(comparator.toUpperCase(), query)
      }

      if (matchWholeProp) {
        obj.IsMatch = comparator.toUpperCase() === query.toUpperCase();
      } else {
        obj.IsMatch = splitQuery.find(y => comparator.toUpperCase().includes(y));
      }

      return obj;
    }).filter(x => x.IsMatch).sort((a, b) => b.MatchLevel - a.MatchLevel);

  // If we're doing a fuzzy search, and there are results that contain ALL queries, reduce the fuzzy search to these results
  if (!matchWholeProp && splitQuery.length > 1) {
    const verboseMatches = arrSearched.filter(x => {
      const originalComparator = propertyName ? x.OriginalElement[propertyName] : x.OriginalElement;

      return !splitQuery.find(y => !originalComparator.toUpperCase().includes(y))
    });

    if (verboseMatches.length) {
      arrSearched = verboseMatches;
    }
  }

  return arrSearched.map(x => x.OriginalElement);
}

_setFlavourLocalization(document.documentElement.dataset.flavour);


const modules = require.context("./modules/components", true, /\.js$/);

modules.keys().forEach(key => {
  const module = modules(key);
  if (!module.default) { return; }

  window.customElements.define(`chip-${key.split("/").pop().replace(".js", "").replace("-","").toLowerCase()}`, module.default);
});

if (location.hash) {
  const closestTab = document.getElementById(location.hash.substring(1))?.closest(".tab-group__tab, chip-tab");

  if (closestTab) {
    closestTab.closest("chip-tabgroup").selectIndex([...closestTab.parentElement.children ?? []].indexOf(closestTab));
  }
}

Razor.UpdateState();

function closeDropdownsFromEvent(ev) {
  let closestInput = ev.target?.closest?.("chip-dropdown")?.parentElement;

  if (closestInput?.nodeName !== "CHIP-INPUT") { closestInput = null; }

  [...document.querySelectorAll("chip-dropdown")].filter(x => (ev.target instanceof Node && !x.contains(ev.target)) && x !== ev.target?.closest?.("chip-dropdown") && !closestInput?.contains(x))
    .forEach(x => x.Close());
}

window.addEventListener('focus', ev => closeDropdownsFromEvent(ev), false);

document.body.addEventListener("mousedown", ev => {
  document.querySelectorAll("chip-contextmenu").forEach(x => x.HandleMenuBlurEvent(ev));

  closeDropdownsFromEvent(ev);
});

function onConfigValueChanged(property, value) {
  switch (property) {
    case "flavour":
      Browser.SetFlavour(value);
      break;
    case "theme":
      Browser.SetColourMode(value);
      break;
    case "colour":
      document.documentElement.dataset.colour = value ?? "blue";
      break;
  }
}

let message = localStorage.getItem("persistent-message");

if (message) {
  message = JSON.parse(message);

  switch (message.type) {
    case "success":
      Toast.ShowSuccess(message.title, message.message, message.options);
      break;
    case "info":
      Toast.ShowInfo(message.title, message.message, message.options);
      break;
    case "warning":
      Toast.ShowWarning(message.title, message.message, message.options);
      break;
    case "error":
      Toast.ShowError(message.title, message.message, message.options);
      break;
  }

  localStorage.removeItem("persistent-message");
}
